/**
 * Reporting contracts — slice 13.
 *
 * **A REPORT IS A DEFINITION PLUS A RUN.** That sentence is the whole file, and
 * it comes straight from the constraint SLICES.md records in the user's own
 * words: *"I need to automate this in future, so keep it that way."* A person
 * clicking Download is one way to start a run; a schedule firing at 00:05 is
 * another; and the generator must not be able to tell which happened.
 *
 * The rejected alternative is named explicitly because it is the shape everybody
 * writes first: **an endpoint that streams a spreadsheet built inline from
 * request parameters.** It works, it demos, and it cannot be scheduled later
 * without being rewritten — when a schedule fires there is no request to read
 * parameters from, no signed-in user to attribute the work to, and no response
 * stream to write the bytes into. Everything below exists so that the unattended
 * path is the *same* path, not a second one bolted alongside:
 *
 *   - {@link reportDefinitionSchema} — what to produce. A **value**, not a row
 *     with a life of its own, and it is copied onto the run rather than pointed
 *     at. A stored, editable definition would rewrite the history of what old
 *     runs covered every time somebody adjusted next month's window.
 *   - {@link reportRunSchema} — one attempt. Carries the definition it ran, what
 *     started it, when, and what came out or why nothing did.
 *   - {@link reportTriggerSchema} — a person **or** a schedule, as a
 *     discriminated union, so "who ran this" can never be blank.
 *   - {@link reportWindowSchema} — half-open `[from, to)` in a named zone,
 *     resolved to instants *before* the run starts and recorded on it.
 *
 * WINDOWS ARE INSTANTS, NEVER PHRASES
 * -----------------------------------------------------------------------------
 * A run never stores "last 30 days". It stores two instants and the zone they
 * were chosen in, because "last 30 days" is only meaningful relative to an
 * unstated moment, and the moment a scheduled job means is not the moment the
 * person reading the sheet six weeks later will assume. {@link ReportWindowRule}
 * is how a *schedule* stays recurring without reintroducing the phrase: the rule
 * lives on the schedule, {@link resolveReportWindow} turns it into instants at
 * tick time, and the instants are what the run and the sheet carry. Slice 14
 * makes the same commitment for its date ranges, and every partition-dating
 * defect in slices 11 and 12 came from the class of mistake this prevents.
 *
 * ONE WORKSHEET PER CHANNEL, AND A FUNCTION — NOT A SCHEMA — ENFORCES IT
 * -----------------------------------------------------------------------------
 * A credit is not the same thing on both channels: the balances are separate,
 * keyed `(tenant_id, channel)`, and Meta bills by 24-hour conversation upstream
 * while Wend counts messages. A single mixed sheet invites a sum down a column
 * whose rows mean two different things. So a credit-usage report holds exactly
 * one section per member of `CHANNEL_VALUES` — no more, no fewer, even when a
 * channel moved nothing — and a section refuses a movement whose channel is not
 * its own.
 *
 * **The superseded version of this paragraph said "fails parsing", and it was
 * not true.** {@link creditUsageReportSchema} and {@link creditUsageSectionSchema}
 * are wire guards, and a credit-usage report never crosses a wire: the generator
 * assembles one in memory and hands it straight to the workbook writer, so
 * nothing in the API ever called `parse` on either and the rule this paragraph
 * described was documentation. The rules now live in
 * {@link creditUsageSectionIssues} and {@link creditUsageChannelCoverageIssues},
 * plain functions that walk the report once; {@link assertCreditUsageReport}
 * runs them on the assembly path, {@link creditUsageReport} calls it, and the
 * schemas' `superRefine`s call the same functions. One implementation, both
 * paths, and no header here claiming a guard that does not run.
 *
 * A channel that moved nothing gets a sheet stating so in a sentence
 * ({@link NO_MOVEMENTS_NOTE}), not an absent sheet and not a lone zero. "This
 * tenant moved no WhatsApp credits in September" and "this workbook forgot about
 * WhatsApp" are different claims, and only the first is ever true here.
 *
 * BUILT FROM THE LEDGER
 * -----------------------------------------------------------------------------
 * Every figure here comes from `credit_ledger`, which is append-only and is the
 * record; `credit_balances` is derived convenience and cannot answer a question
 * about a window at all. Note the consequence for the sheet: this file defines
 * **no second money type, no second channel enum and no second rate helper**. A
 * movement row *is* a {@link CreditLedgerEntry}, the totals are folded with
 * `sumLedgerEntries`, and the arithmetic that says what a `release` does to a
 * balance is imported from `./credit.js` rather than restated. Two
 * implementations of that arithmetic drift, and the way they drift is that the
 * report starts disagreeing with the balance screen with no way to tell which is
 * lying.
 *
 * One counting note for whoever writes the query, because the reflex in this
 * repo is now `countDistinct(message_id)` and here it would be wrong: that rule
 * exists because SES, SNS and Meta all re-notify, so `delivery_events` holds
 * several rows per message. `credit_ledger` does not — `idempotency_key` is
 * unique and the balance moves only when a row was actually inserted, which is
 * the whole replay guard. Counting distinct message ids over the ledger would
 * silently collapse a message's `reserve` and its `consume` into one, and a
 * report that hides half the movements is worse than one that never ran.
 *
 * The vocabulary rule from `./credit.js` travels with it: no field, and **no
 * column heading**, may say cost, spend, charge or price. A credit is Wend's
 * metering unit and has no exchange rate with anybody's AWS or Meta invoice, and
 * a column headed "Spend" over a number that is not money is a billing dispute
 * waiting to be started by a spreadsheet nobody can take back.
 *
 * THIS FILE IS A LEAF
 * -----------------------------------------------------------------------------
 * It imports `./credit.js` (the ledger vocabulary it reports on) and
 * `./campaign.js` (`timeZoneSchema`, the one place an IANA zone is validated),
 * and neither imports it back. Files that report over a window import it —
 * `./analytics.js` since slice 14, `./meta-ads-surfaces.js` since slice 44 —
 * and nothing it imports imports them; the arrow only ever points in here. That
 * direction is not stylistic: slices 8, 10 and 12 each hit
 * a cycle between two modules that both build zod schemas at load, and it does
 * not surface as a review comment — it surfaces as `z.enum(undefined)` thrown at
 * import, before a single test runs. Defining a second `timeZoneSchema` here to
 * avoid the arrow would have been worse than the cycle it avoided: two opinions
 * about what a valid zone is, one of which is the one the scheduler uses.
 *
 * The four vocabularies a Postgres type is minted from — `REPORT_KIND_VALUES`,
 * `REPORT_FORMAT_VALUES`, `REPORT_RUN_STATUS_VALUES`, `REPORT_TRIGGER_KIND_VALUES`
 * — live in `../enums.js` with every other pg-mirrored enum, so `@wend/db` reads
 * them from where it reads the rest. {@link REPORT_FAILURE_CODE_VALUES} stays
 * here: it is not a pg enum, because a failure code is the kind of vocabulary
 * that gains a member during an incident and must not need a migration to do it.
 */
import { z } from 'zod';

import { isoDateTimeSchema, paginated, paginationQuerySchema, uuidSchema } from '../common.js';
import {
  CHANNEL_VALUES,
  LEDGER_ENTRY_TYPE_VALUES,
  REPORT_KIND_VALUES,
  channelSchema,
  ledgerEntryTypeSchema,
  reportFormatSchema,
  reportKindSchema,
  reportRunStatusSchema,
  type Channel,
  type LedgerEntryType,
  type ReportFormat,
  type ReportKind,
} from '../enums.js';
import { timeZoneSchema } from './campaign.js';
import {
  ZERO_CREDIT_AMOUNTS,
  creditLedgerEntrySchema,
  sumLedgerEntries,
  type CreditAmounts,
  type CreditLedgerEntry,
} from './credit.js';
import { SURFACE_CAPABILITY, SURFACE_ORDER, surfaceIsBuilt } from './surface.js';

/* -------------------------------------------------------------------------- */
/* Titles and file types                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What each report is called, in the words a reader recognises.
 *
 * Total over {@link ReportKind}, so a second kind cannot be added without a
 * title — a workbook whose first line is `credit_usage` is a workbook that was
 * named by the schema rather than for the person opening it.
 */
export const REPORT_TITLES = {
  credit_usage: 'Credit usage',
  surface_results: 'Results by surface',
} as const satisfies Readonly<Record<ReportKind, string>>;

/** The MIME type each format is stored and served with. Total over the enum. */
export const REPORT_CONTENT_TYPES = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as const satisfies Readonly<Record<ReportFormat, string>>;

/** The extension each format's file carries. Total over the enum. */
export const REPORT_FILE_EXTENSIONS = {
  xlsx: 'xlsx',
} as const satisfies Readonly<Record<ReportFormat, string>>;

/* -------------------------------------------------------------------------- */
/* Zoned time — the arithmetic a window is built from                          */
/* -------------------------------------------------------------------------- */

/**
 * The offset, in milliseconds, that `timezone` was at during `instant`.
 *
 * `Intl` is a platform global rather than a dependency, so this keeps this
 * package's "nothing at runtime but zod" promise while asking the only authority
 * that knows when Chile last moved its clocks.
 *
 * **It fails closed — `null` when the platform cannot answer.** That is the
 * opposite of `isKnownTimeZone` in `./campaign.js`, deliberately: that function
 * is deciding whether to render a warning, and refusing `Asia/Kolkata` on an
 * ICU-less runtime would make the product unusable rather than safe. This one is
 * deciding *which rows are in the report*, and a window computed against the
 * wrong clock is a spreadsheet that is confidently wrong — the worst artefact
 * this slice can produce, because it outlives the screen it came from. A caller
 * that gets `null` records {@link REPORT_FAILURE_CODES.window_unresolvable} and
 * runs nothing.
 */
export function zoneOffsetMs(instant: Date, timezone: string): number | null {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return null;
  if (Number.isNaN(instant.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(instant);

    const read = (type: string): number | null => {
      const part = parts.find((candidate) => candidate.type === type);
      if (part === undefined) return null;
      const value = Number.parseInt(part.value, 10);
      return Number.isNaN(value) ? null : value;
    };

    const year = read('year');
    const month = read('month');
    const day = read('day');
    const rawHour = read('hour');
    const minute = read('minute');
    const second = read('second');
    if (
      year === null ||
      month === null ||
      day === null ||
      rawHour === null ||
      minute === null ||
      second === null
    ) {
      return null;
    }

    // Older V8 renders midnight as "24" under `hour12: false` (the h24 cycle),
    // with the date still on the day that is beginning. `localHourInTimeZone`
    // in ./campaign.js normalises the same way and for the same reason: reaching
    // for `hourCycle` instead would break on every runtime whose type
    // definitions predate the option.
    const hour = rawHour === 24 ? 0 : rawHour;

    const asIfUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    // The formatted parts have no milliseconds, so drop them from both sides
    // rather than letting a sub-second remainder land in the offset.
    return asIfUtc - (instant.getTime() - instant.getUTCMilliseconds());
  } catch {
    return null;
  }
}

/**
 * The instant at which the local day (or month) containing `instant` began in
 * `timezone` — **the first instant that reads as that boundary**, never the
 * second one. `null` when the platform cannot say.
 *
 * The offset at the *instant we are converting from* is not necessarily the
 * offset at the boundary we are converting *to* — that is the whole of the DST
 * problem in one sentence. India, the launch market, has no DST and would never
 * exercise any of the arithmetic below, which is exactly why it is written now
 * rather than after somebody in Santiago files the bug.
 *
 * **The superseded version took two passes and returned the LATER of two
 * ambiguous midnights, and that was a defect, not a nicety.** In a zone whose
 * clocks fall back *through* local midnight — America/Havana on 2026-11-01,
 * Atlantic/Azores on 2026-10-25 — local 00:00 happens twice, an hour apart. The
 * old shape started from the offset in force at `instant`, which for anything
 * later in that day (or month) is standard time, guessed the boundary against
 * it, found the guess consistent, and stopped: the *second* midnight. Two things
 * broke, both of them silently:
 *
 *   - {@link resolveReportWindow} walks backwards from `to − 1ms` to find
 *     `from`. When `to` is the second midnight, `to − 1ms` is still inside the
 *     same local day, so the walk never leaves it and the whole window collapses
 *     to **one hour**. "The month that just ended" resolved to
 *     `[2026-11-01T04:00Z, 2026-11-01T05:00Z)` in Havana — one hour of 1
 *     November under a label saying October — and "the day that just ended"
 *     resolved to the same hour of the day it was asked on.
 *   - a day's first repeated hour fell outside its own day, so a send at
 *     2026-11-01T04:30Z was reported under 31 October.
 *
 * So the boundary is chosen rather than guessed: every offset in force within a
 * day either side of the boundary is tried, the candidates that genuinely read
 * back as that local wall-clock reading are kept, and the **earliest** wins.
 * Consecutive windows still tile exactly — one window's `to` is the next one's
 * `from` by construction — and now they tile in the right place.
 *
 * **The hole it leaves, stated rather than hidden:** in a zone whose clocks jump
 * *over* local midnight, no instant maps to 00:00 at all. No candidate reads
 * back, and this returns the first instant that exists on that day — the
 * transition itself — exactly as the superseded version did. That is acceptable
 * for the same reason it always was: contiguity is what the report depends on,
 * and deriving one boundary from the other keeps it whatever the zone did.
 */
export function zonedStartOf(
  instant: Date,
  timezone: string,
  granularity: 'day' | 'month',
): Date | null {
  const offset = zoneOffsetMs(instant, timezone);
  if (offset === null) return null;

  const local = new Date(instant.getTime() + offset);
  const startAsIfUtc = Date.UTC(
    local.getUTCFullYear(),
    local.getUTCMonth(),
    granularity === 'month' ? 1 : local.getUTCDate(),
  );

  /*
   * The offsets that could apply at the boundary. A transition is at most a
   * couple of hours and there is at most one within a day of any boundary, so
   * probing a day either side of the first guess sees both sides of it — which
   * is the whole reason `offset`, taken as far away as the middle of a month,
   * is not trusted on its own.
   */
  const firstGuess = startAsIfUtc - offset;
  const offsets = new Set<number>([offset]);
  for (const probe of [firstGuess - MS_PER_DAY, firstGuess, firstGuess + MS_PER_DAY]) {
    const probed = zoneOffsetMs(new Date(probe), timezone);
    if (probed === null) return null;
    offsets.add(probed);
  }

  let earliestReal: number | null = null;
  let latestCandidate: number | null = null;
  for (const candidate of offsets) {
    const at = startAsIfUtc - candidate;
    latestCandidate = latestCandidate === null ? at : Math.max(latestCandidate, at);

    // Does this instant actually read back as the boundary? An ambiguous
    // midnight answers yes twice and a skipped one answers no at all.
    const actual = zoneOffsetMs(new Date(at), timezone);
    if (actual === null) return null;
    if (at + actual !== startAsIfUtc) continue;
    earliestReal = earliestReal === null ? at : Math.min(earliestReal, at);
  }

  if (earliestReal !== null) return new Date(earliestReal);
  // The boundary does not exist in this zone: the clocks jumped over it. The
  // latest candidate is the transition instant — the first one the day has.
  return latestCandidate === null ? null : new Date(latestCandidate);
}

/**
 * `YYYY-MM-DD` for an instant as seen in `timezone`, or the UTC date when the
 * platform cannot say.
 *
 * It falls back rather than returning `null` because every caller is putting the
 * result somewhere a reader will look — a filename, a caption — and a blank
 * where a date belongs is the failure mode decision 4 of this slice exists to
 * prevent. The fallback is UTC, which is at worst off by a day and is never
 * silent: {@link formatInstantInZone}, which renders the same instant on the
 * sheet, names the zone it managed to use.
 */
export function zonedIsoDate(instant: Date, timezone: string): string {
  const offset = zoneOffsetMs(instant, timezone);
  const shifted = offset === null ? instant : new Date(instant.getTime() + offset);
  const year = String(shifted.getUTCFullYear()).padStart(4, '0');
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const day = String(shifted.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * An instant as a person reads it, in the report's zone and naming the zone —
 * e.g. `01 Sep 2026, 00:00 (Asia/Kolkata)`.
 *
 * **The zone name is part of the string, not a separate cell.** A timestamp in a
 * spreadsheet with no zone beside it is read as local time by whoever opens it,
 * and this workbook is specified to be openable by somebody who never saw the
 * filters. When `Intl` cannot answer, it renders the ISO instant and says
 * `(UTC)` — still unambiguous, visibly not the requested zone.
 */
export function formatInstantInZone(instant: Date, timezone: string): string {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return `${instant.toISOString()} (UTC)`;
  }
  try {
    const text = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour12: false,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(instant);
    return `${text} (${timezone})`;
  } catch {
    return `${instant.toISOString()} (UTC)`;
  }
}

/* -------------------------------------------------------------------------- */
/* The window                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The longest window a single run may cover.
 *
 * A bound rather than none, because an unbounded window is the shape that wedges
 * an unattended job: a run that reads five years of ledger inside a scheduler
 * tick is slow exactly when the scheduler is already behind, and slice 8's
 * `TENANT_BUDGET_MS` is the neighbour that learned this the expensive way. A
 * year and a day is generous enough that no legitimate request meets it and
 * small enough that a typo — a `from` in 1970, a millisecond timestamp pasted as
 * seconds — is refused at the contract rather than discovered by a timeout.
 */
export const MAX_REPORT_WINDOW_DAYS = 366;

const MS_PER_DAY = 86_400_000;

/**
 * How much longer than `MAX_REPORT_WINDOW_DAYS × 24h` a window of that many
 * *calendar* days may be, because a calendar day is not always 24 hours.
 *
 * **Without this the instant check below was a second, stricter policy than the
 * calendar check callers are told about, and it fired on ranges the calendar
 * check had just allowed.** A window that contains a fall-back and no
 * spring-forward is one offset shift longer than the arithmetic says: 366
 * calendar days from 2026-03-09 to 2027-03-09 in `America/New_York` resolves to
 * 366 days *and one hour*, so `analyticsRangeRequestSchema` accepted it, this
 * refused it, and the caller — whose only failure channel is `null` — told a US
 * tenant their timezone could not be resolved while an Indian tenant asking for
 * the identical dates got a report.
 *
 * Two hours, because that is the largest shift any current IANA zone applies:
 * `Antarctica/Troll` moves between UTC+00 and UTC+02, most zones move one hour,
 * and `Australia/Lord_Howe` moves thirty minutes. So this is slack for zone
 * arithmetic and nothing else — it does not widen the policy by a day, and a
 * caller asking for 367 calendar days is still refused in every zone.
 */
const MAX_ZONE_OFFSET_SHIFT_MS = 2 * 60 * 60 * 1000;

/** The instant-level backstop: the stated cap, plus room for one offset shift. */
const MAX_REPORT_WINDOW_MS = MAX_REPORT_WINDOW_DAYS * MS_PER_DAY + MAX_ZONE_OFFSET_SHIFT_MS;

/**
 * The period a report covers: **half-open `[from, to)`**, in a named IANA zone.
 *
 * Half-open is not a detail. It is the only convention under which consecutive
 * windows tile the timeline without a gap and without an overlap, so a movement
 * at exactly midnight lands in precisely one report — never in both September
 * and October, never in neither. An inclusive `to` cannot say "up to midnight"
 * without either double-counting the boundary or excluding the last second, and
 * whichever it picks is invisible until somebody adds two months together.
 *
 * **The zone is stored as well as the instants**, for the reason `./campaign.js`
 * stores a campaign's zone beside its UTC instant: the instants are what the SQL
 * compares and what makes the window reproducible, and the zone is what the
 * sheet prints and what the boundaries meant to the person who chose them.
 * Storing only the instants loses the answer to "did they mean the 1st in
 * Mumbai?", which is the question anybody re-running the report is asking.
 */
export const reportWindowSchema = z
  .object({
    /** Inclusive lower bound. */
    from: isoDateTimeSchema,
    /** **Exclusive** upper bound. A movement at exactly `to` is in the next window. */
    to: isoDateTimeSchema,
    /** The zone the boundaries were chosen in, and the zone the sheet prints. */
    timezone: timeZoneSchema,
  })
  .strict()
  .superRefine((window, ctx) => {
    const from = Date.parse(window.from);
    const to = Date.parse(window.to);
    if (Number.isNaN(from) || Number.isNaN(to)) return;

    // Not `<=`: an empty window is not a small report, it is a report that
    // cannot contain anything, and the only way to ask for one is by mistake.
    if (to <= from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'A report window ends after it starts — [from, to) with to > from',
      });
      return;
    }
    // The backstop, deliberately looser than the calendar-day cap by one zone
    // shift — see MAX_ZONE_OFFSET_SHIFT_MS. A check here that were stricter than
    // the one a form can render is a refusal nobody can act on.
    if (to - from > MAX_REPORT_WINDOW_MS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: `A report window covers at most ${MAX_REPORT_WINDOW_DAYS} days; ask for a shorter period`,
      });
    }
  });
export type ReportWindow = z.infer<typeof reportWindowSchema>;

/** The window's boundaries as instants. One parse, so no caller re-parses. */
export function reportWindowInstants(window: ReportWindow): { from: Date; to: Date } {
  return { from: new Date(window.from), to: new Date(window.to) };
}

/**
 * Whether an instant falls in the window. **`from <= t < to`.**
 *
 * The one definition of the boundary, so the generator's SQL, the front-end's
 * copy and the suite that proves a movement on the boundary lands in exactly one
 * bucket are all reading the same rule. A second `>=`/`<=` written by hand
 * somewhere else is how a boundary movement gets counted twice.
 */
export function windowContains(window: ReportWindow, instant: Date): boolean {
  const time = instant.getTime();
  return time >= Date.parse(window.from) && time < Date.parse(window.to);
}

/** Whole days the window spans. For copy and for run budgeting, not for SQL. */
export function reportWindowDurationDays(window: ReportWindow): number {
  return (Date.parse(window.to) - Date.parse(window.from)) / MS_PER_DAY;
}

/**
 * The window as one sentence, with both boundaries and the zone.
 *
 * It says **"up to but not including"** in words rather than trusting a bracket.
 * `[1 Sep, 1 Oct)` is unambiguous to about four people per organisation; the
 * rest read it as an inclusive range and quietly conclude the report is missing
 * a day. This string goes on every sheet.
 */
export function describeReportWindow(window: ReportWindow): string {
  const { from, to } = reportWindowInstants(window);
  return `${formatInstantInZone(from, window.timezone)} up to but not including ${formatInstantInZone(to, window.timezone)}`;
}

/* -------------------------------------------------------------------------- */
/* Recurring windows — how a schedule stays a schedule                         */
/* -------------------------------------------------------------------------- */

/**
 * The window rules a schedule may hold.
 *
 *   previous_day   — the local day that has just ended
 *   previous_month — the local month that has just ended
 *
 * **Both are closed periods.** There is deliberately no `last_30_days` and no
 * `month_to_date`: a rule that ends "now" produces a different answer every time
 * it runs, so two runs of the same schedule are not comparable and neither is
 * reproducible — which is the phrase-shaped window this slice exists to refuse,
 * merely spelled as an enum. A rule that ends at a boundary can be re-run next
 * year and give the same workbook.
 *
 * The rule lives on the *schedule*. The run stores the instants the rule
 * resolved to, never the rule, so a schedule that is later edited cannot change
 * what a past run covered.
 */
export const REPORT_WINDOW_RULE_VALUES = ['previous_day', 'previous_month'] as const;
export const reportWindowRuleSchema = z.enum(REPORT_WINDOW_RULE_VALUES);
export type ReportWindowRule = z.infer<typeof reportWindowRuleSchema>;

/** How each rule is described where a person picks one. */
export const REPORT_WINDOW_RULE_LABELS = {
  previous_day: 'The day that just ended',
  previous_month: 'The month that just ended',
} as const satisfies Readonly<Record<ReportWindowRule, string>>;

/**
 * Turns a rule and the moment a schedule fired into an explicit window, or
 * `null` when the zone could not be resolved.
 *
 * The earlier boundary is computed from **one millisecond before the later one**
 * rather than by subtracting a day or a month. Subtracting is the version that
 * is wrong twice a year — 24 hours before a DST boundary is 23:00 or 01:00, not
 * midnight — and, worse, it is wrong in a way that leaves a gap or an overlap
 * between two consecutive runs, so a movement is either reported twice or never.
 * Deriving one boundary from the other makes contiguity structural: this run's
 * `from` is the previous run's `to` by construction.
 *
 * Returns `null` rather than falling back to UTC. A scheduled run that cannot
 * establish its own window must fail visibly — see
 * {@link REPORT_FAILURE_CODES.window_unresolvable} — because a report silently
 * bucketed against the wrong clock is indistinguishable from a correct one.
 */
export function resolveReportWindow(
  rule: ReportWindowRule,
  firedAt: Date,
  timezone: string,
): ReportWindow | null {
  const granularity = rule === 'previous_month' ? 'month' : 'day';
  const to = zonedStartOf(firedAt, timezone, granularity);
  if (to === null) return null;
  const from = zonedStartOf(new Date(to.getTime() - 1), timezone, granularity);
  if (from === null) return null;
  return { from: from.toISOString(), to: to.toISOString(), timezone };
}

/**
 * The window immediately before `window` under the same rule, or `null` when its
 * zone cannot be resolved.
 *
 * This is what lets a schedule catch up on a period it missed. {@link
 * resolveReportWindow} answers "which window does *this moment* belong to", and
 * a scheduler that only ever asks that has no way back to a window it slept
 * through: a tick at 00:10 resolves the day that just ended and nothing tells it
 * that the day before that never got a report. Walking backwards from a window
 * rather than subtracting 24 hours from an instant is what keeps that walk
 * correct across a DST boundary and across months of different lengths —
 * `firedAt` here is the *start* of the given window, so the rule's own zoned
 * arithmetic produces the period that tiles exactly against it, with no gap and
 * no overlap.
 *
 * Deliberately one step. How far back to walk is a policy decision and belongs
 * to the caller that knows how much unattended backfill is acceptable; this
 * function has no opinion and cannot run away with itself.
 */
export function previousReportWindow(
  rule: ReportWindowRule,
  window: ReportWindow,
): ReportWindow | null {
  return resolveReportWindow(rule, new Date(Date.parse(window.from)), window.timezone);
}

/* -------------------------------------------------------------------------- */
/* The definition — what to produce                                            */
/* -------------------------------------------------------------------------- */

/**
 * Everything the generator needs, and nothing about who asked.
 *
 * That omission is the load-bearing part: hand this to the generator and it
 * cannot tell whether a person or a schedule is waiting, which is what makes the
 * two paths provably the same path. The suite drives both and compares the
 * bytes; if the generator could read the trigger, that assertion would only
 * prove the two happened to agree today.
 *
 * `tenantId` is on the definition because an unattended run has no session to
 * infer it from. It is **not** on {@link createReportRunRequestSchema}: a
 * tenant-facing request must take its tenant from the authenticated principal,
 * never from the body, or the field becomes the id somebody edits to read
 * another workspace's report. Decision 5 of this slice — real bank addresses and
 * real phone numbers — is enforced by the request shape not having the field to
 * begin with, and by RLS underneath it.
 */
export const reportDefinitionSchema = z
  .object({
    kind: reportKindSchema,
    format: reportFormatSchema,
    /** Whose data. Filled from the principal on a request; from the schedule row otherwise. */
    tenantId: uuidSchema,
    window: reportWindowSchema,
  })
  .strict();
export type ReportDefinition = z.infer<typeof reportDefinitionSchema>;

/* -------------------------------------------------------------------------- */
/* The trigger — who or what started it                                        */
/* -------------------------------------------------------------------------- */

/** A schedule's name, dotted like a job name: `report.credit_usage.nightly`. */
export const reportScheduleNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(
    /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/,
    'A schedule name is lower-case and dotted, e.g. report.credit_usage.nightly',
  );

/**
 * The nightly credit-usage schedule's name, as one constant.
 *
 * A literal typed at both ends is a literal that gets typed differently at one
 * of them, and the run record would then say a schedule ran that nothing in the
 * process answers to — attribution that looks complete and is wrong.
 */
export const CREDIT_USAGE_NIGHTLY_SCHEDULE = 'report.credit_usage.nightly';

const userTriggerObject = z
  .object({
    kind: z.literal('user'),
    /**
     * Null once the account is gone. **Never null at write time.**
     *
     * The same two-field shape as `adminAuditActorSchema`, for the same reason:
     * every audit-ish foreign key here is `ON DELETE SET NULL`, so a pointer on
     * its own would erase the attribution on every run a person ever started the
     * day they were offboarded. `ON DELETE RESTRICT` was the rejected
     * alternative — it turns a run record into a reason accounts can never be
     * removed, and the pressure then goes on deleting the records.
     */
    userId: uuidSchema.nullable(),
    /** Copied at write time. Survives the pointer being nulled. */
    userEmail: z.string().email(),
  })
  .strict();

const scheduleTriggerObject = z
  .object({
    kind: z.literal('schedule'),
    /** Which schedule. Matches a name the process actually registers. */
    scheduleName: reportScheduleNameSchema,
  })
  .strict();

/**
 * What started a run — a person, or a schedule. Never neither.
 *
 * A discriminated union rather than a nullable `userId` beside a nullable
 * `scheduleName`, because that pair can express both-null and both-set. Both-null
 * is a run nobody can account for, and it is what every unattended run would be
 * written as the first time somebody forgets to pass an actor; both-set is a
 * question with two answers. Neither is constructible here.
 */
export const reportTriggerSchema = z.discriminatedUnion('kind', [
  userTriggerObject,
  scheduleTriggerObject,
]);
export type ReportTrigger = z.infer<typeof reportTriggerSchema>;
export type ReportUserTrigger = z.infer<typeof userTriggerObject>;
export type ReportScheduleTrigger = z.infer<typeof scheduleTriggerObject>;

/**
 * "Started by …", in words, for the caption on every sheet.
 *
 * A schedule is named as a schedule rather than rendered as a blank or as
 * "system": a reader who sees a blank assumes the field was not filled in, and
 * "system" hides which of several schedules produced the file.
 *
 * **This string can contain a real email address**, so it belongs on the sheet
 * and in the run record and nowhere else. It must not be interpolated into a log
 * line — the repo-wide rule is that no real address is ever logged, and a helper
 * that returns one is exactly how one ends up in a log by accident.
 */
export function describeReportTrigger(trigger: ReportTrigger): string {
  return trigger.kind === 'user' ? trigger.userEmail : `the ${trigger.scheduleName} schedule`;
}

/* -------------------------------------------------------------------------- */
/* Failure — a failed run is a failed run, not a missing file                   */
/* -------------------------------------------------------------------------- */

/**
 * Why a run stopped.
 *
 *   generator_failed      — the workbook could not be built (a read threw, a
 *                           row would not parse)
 *   storage_unavailable   — it was built and could not be stored. Distinct from
 *                           the above because one is our arithmetic and the
 *                           other is a transport, and the operator response is
 *                           different: retry the second, investigate the first.
 *   timed_out             — it exceeded its **wall-clock** budget. Not "failed for
 *                           an unknown reason": a run that is always slow is a
 *                           capacity conversation, and it is invisible if it is
 *                           filed under generator_failed.
 *   window_too_large      — the window holds more rows than one workbook can
 *                           carry, and the generator refused it rather than
 *                           truncating. **Nothing was slow.** This member exists
 *                           because the refusal used to be filed under
 *                           `timed_out`, which told a tenant the report "took too
 *                           long" after a refusal that took under a second — so
 *                           they retried at a quieter hour, got the identical
 *                           refusal, and had no way to learn that a shorter
 *                           period was the remedy. A code whose sentence sends
 *                           the reader somewhere useless is worse than no code.
 *   window_unresolvable   — the zone could not be resolved, so the window could
 *                           not be established. The run refuses rather than
 *                           silently bucketing against UTC.
 *   interrupted           — the process ended mid-run. Written by whatever finds
 *                           a **running** row from a previous boot, so a restart
 *                           leaves failed runs rather than runs that are
 *                           permanently, falsely, "generating…".
 *   abandoned_in_queue    — it never started. The run sat `queued` for longer
 *                           than any backlog this platform produces, and the
 *                           in-process queue that was holding it is gone.
 *                           **Nothing was built and nothing ran**, which is why
 *                           it is not `interrupted`: the superseded vocabulary
 *                           had one code for both, so a report that waited six
 *                           hours behind a row of dataset imports was told "this
 *                           run stopped when the service restarted" — a cause
 *                           the reader can check and find never happened — and
 *                           the operator was sent looking for a crash instead of
 *                           at a saturated runner. Same remedy, different fact,
 *                           and the fact is the half an operator acts on. It is
 *                           the `timed_out`/`window_too_large` split again, on
 *                           the sweep.
 *
 * Every member is **reachable for the condition it names**, and that is checked
 * by the suite rather than assumed: `verify-reporting` forces a write-side
 * storage failure and asserts `storage_unavailable`, `verify-reports` forces the
 * row cap and asserts `window_too_large` and forces the run budget and asserts
 * `timed_out`, and both `verify-reports` and `verify-report-runs` plant a run
 * older than each sweep cutoff and assert `interrupted` and
 * `abandoned_in_queue` separately. Several of those were, at one point, recorded
 * as something else — a code nothing can produce is decoration, and a decorative
 * code is discovered only when somebody needs it during an incident.
 *
 * **There is no `no_data` member, and there must never be one.** A window
 * containing no movements is a *successful* report of a quiet month, and the
 * sheet says so in a sentence. Filing it as a failure is the zero-versus-absent
 * mistake this codebase keeps catching — "nothing happened" and "we could not
 * find out what happened" are different statements and only one of them means
 * anybody has to do something.
 *
 * Plain `text` in the database rather than a pg enum: a failure vocabulary gains
 * a member during an incident, and needing a migration at that moment is how the
 * new case gets filed under an existing code instead.
 */
export const REPORT_FAILURE_CODE_VALUES = [
  'generator_failed',
  'storage_unavailable',
  'timed_out',
  'window_too_large',
  'window_unresolvable',
  'interrupted',
  'abandoned_in_queue',
] as const;
export const reportFailureCodeSchema = z.enum(REPORT_FAILURE_CODE_VALUES);
export type ReportFailureCode = z.infer<typeof reportFailureCodeSchema>;

/** Named access, so a caller writes `REPORT_FAILURE_CODES.timed_out`, not a string. */
export const REPORT_FAILURE_CODES = {
  generator_failed: 'generator_failed',
  storage_unavailable: 'storage_unavailable',
  timed_out: 'timed_out',
  window_too_large: 'window_too_large',
  window_unresolvable: 'window_unresolvable',
  interrupted: 'interrupted',
  abandoned_in_queue: 'abandoned_in_queue',
} as const satisfies Readonly<Record<ReportFailureCode, ReportFailureCode>>;

export const MAX_REPORT_FAILURE_MESSAGE_LENGTH = 500;

/**
 * What a person is told when a run fails, per code.
 *
 * The sentence says what happened and what to do, because the alternative — the
 * raw message from whatever threw — is the thing that ends up on a screen
 * carrying a connection string or a bucket name.
 *
 * **Each sentence has to send the reader somewhere that helps, or the code it
 * belongs to is worse than no code at all.** `timed_out` and `window_too_large`
 * are two sentences rather than one for exactly that reason: "took too long"
 * invites a retry at a quieter hour, which is the right advice when a run was
 * slow and useless advice when it was refused for size in under a second. The
 * remedy for the second is a shorter period and nothing else, so it says so and
 * says nothing about time. `interrupted` and `abandoned_in_queue` are two
 * sentences for the same reason one layer along: one names a restart, the other
 * names a queue, and only one of them is ever what happened.
 *
 * **NO SENTENCE HERE MAY CLAIM THE REPORT WAS BUILT UNLESS IT WAS.**
 * `storage_unavailable` used to open *"The report was built but could not be
 * saved"*, and that is false in the ordinary shape of a store-first failure —
 * the volume is full, `put` rejects before the writer has produced a single
 * byte, and the workbook is destroyed part-way through. It is the shape
 * `verify-reporting` section 9 injects, and the same section asserts three lines
 * later that no bytes were left behind. A sentence promising a customer their
 * report exists and only needs re-saving, on a run where nothing was ever
 * written, is the zero-versus-absent defect wearing a different hat.
 */
export const REPORT_FAILURE_SUMMARIES = {
  generator_failed: 'This report could not be built. Nothing was changed; try again, and tell us if it keeps failing.',
  storage_unavailable: 'This report could not be saved, so nothing was kept. Try again in a few minutes.',
  timed_out: 'This report took too long to build. Try a shorter period.',
  window_too_large:
    'This period holds too many credit movements to fit one workbook, so nothing was built. Ask for a shorter period.',
  window_unresolvable: 'The period for this report could not be worked out in its timezone, so nothing was read.',
  interrupted: 'This run stopped when the service restarted. Nothing was saved — start it again.',
  abandoned_in_queue:
    'This run was still waiting to start hours after it was asked for, so it was given up on. Nothing was built — start it again.',
} as const satisfies Readonly<Record<ReportFailureCode, string>>;

/**
 * The failure, on the run.
 *
 * `message` is required and non-empty. A failure with an empty reason is the
 * state that renders as a shrug, and the whole point of `failed` being its own
 * status is that the run can answer "why". It is operator-facing detail — a
 * message a developer can act on — and it must never carry a credential, a
 * connection string or a real recipient address; {@link REPORT_FAILURE_SUMMARIES}
 * is what a tenant is shown.
 */
export const reportFailureSchema = z
  .object({
    code: reportFailureCodeSchema,
    message: z.string().trim().min(1).max(MAX_REPORT_FAILURE_MESSAGE_LENGTH),
  })
  .strict();
export type ReportFailure = z.infer<typeof reportFailureSchema>;

/* -------------------------------------------------------------------------- */
/* The artifact — what came out                                                */
/* -------------------------------------------------------------------------- */

/**
 * The file a successful run produced.
 *
 * `storageKey` is a plain bounded string here and **is not re-validated against
 * the key alphabet**, because that alphabet is `assertValidObjectKey` in the
 * API's storage port and it is the transport's traversal guard. A second
 * implementation of it in this package would be a second opinion about what a
 * safe key is, and the dangerous kind of drift is the one where the looser copy
 * is the one a caller reaches for. {@link reportObjectKeySegments} is what this
 * package contributes: the *shape* of the key, in segments the API joins and
 * validates with its own function.
 *
 * `byteSize` is `positive()`, not `min(0)`. A zero-byte workbook is not a small
 * report — it is a file nothing can open, and a run that produced one succeeded
 * at nothing. Refusing it here means the failure shows up as a failed run rather
 * than as a download that opens to an error message in Excel.
 */
export const reportArtifactSchema = z
  .object({
    /** Tenant-scoped. See {@link reportObjectKeySegments}. */
    storageKey: z.string().trim().min(1).max(512),
    format: reportFormatSchema,
    contentType: z.string().trim().min(1).max(160),
    /** Bytes. Positive — see above. */
    byteSize: z.number().int().positive(),
    /** What the browser saves it as. See {@link reportFileName}. */
    fileName: z.string().trim().min(1).max(160),
  })
  .strict()
  .superRefine((artifact, ctx) => {
    if (artifact.contentType !== REPORT_CONTENT_TYPES[artifact.format]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contentType'],
        message: `A ${artifact.format} report is served as ${REPORT_CONTENT_TYPES[artifact.format]}`,
      });
    }
  });
export type ReportArtifact = z.infer<typeof reportArtifactSchema>;

/**
 * The key segments a report's bytes live under, **relative to nothing** — the
 * API joins them with its own `objectKey()`, which validates each segment.
 *
 *     objectKey('tenants', tenantId, ...reportObjectKeySegments(runId, kind, format))
 *
 * Keyed by **run id**, not by window or by kind. Two runs of the same definition
 * are two files, and the second must not overwrite the first: the first one is
 * what somebody already downloaded and may be about to ask questions about.
 * Every segment is built from an id and constants we control, never from
 * anything a person typed, which is the rule the key alphabet exists to survive.
 */
export function reportObjectKeySegments(
  runId: string,
  kind: ReportKind,
  format: ReportFormat,
): readonly string[] {
  return ['reports', runId, `${kind.replace(/_/g, '-')}.${REPORT_FILE_EXTENSIONS[format]}`];
}

/**
 * The name the file is downloaded as, e.g. `credit-usage-2026-09-01-to-2026-09-30.xlsx`.
 *
 * **The dates in the name are the inclusive ones** — the last day the window
 * actually covers, not the exclusive `to`. That is a deliberate mismatch with
 * {@link describeReportWindow}, which prints the true half-open boundaries, and
 * it should not be "fixed" in either direction. A filename is read at a glance
 * by somebody deciding which of four downloads to open, and
 * `...-to-2026-10-01.xlsx` on a file containing nothing from October is the kind
 * of small lie that gets a correct report deleted as wrong. The sheet inside is
 * where the exact boundaries are stated, and it is stated there precisely
 * because the filename cannot carry that nuance.
 *
 * "Last included day" means the day the last included *instant* falls on **in
 * the window's own zone**, which is only the same thing as "the day before `to`"
 * when the window is aligned to that zone. A window a caller assembled by hand
 * out of UTC midnights while naming an Indian zone genuinely does extend into
 * the next Indian day, and this names that day — the filename is not the place
 * to paper over a window that was built wrong.
 */
export function reportFileName(
  kind: ReportKind,
  window: ReportWindow,
  format: ReportFormat,
): string {
  const { from, to } = reportWindowInstants(window);
  const firstDay = zonedIsoDate(from, window.timezone);
  const lastDay = zonedIsoDate(new Date(to.getTime() - 1), window.timezone);
  return `${kind.replace(/_/g, '-')}-${firstDay}-to-${lastDay}.${REPORT_FILE_EXTENSIONS[format]}`;
}

/* -------------------------------------------------------------------------- */
/* The run                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * What every run carries whatever became of it.
 *
 * `definition` is embedded rather than referenced. A definition that lived in
 * its own table and was edited would rewrite what every past run claims to have
 * covered — the report equivalent of a stored `delivered_count`, and wrong for
 * the same reason: a snapshot's inputs have to be as immutable as its output.
 *
 * `requestedAt` is separate from `startedAt` on purpose. The gap between them is
 * queue latency, and it is the number that answers "why did the 00:05 report
 * arrive at 09:00" without anybody having to guess.
 */
const reportRunBase = {
  id: uuidSchema,
  definition: reportDefinitionSchema,
  trigger: reportTriggerSchema,
  requestedAt: isoDateTimeSchema,
};

const queuedRunObject = z
  .object({
    ...reportRunBase,
    status: z.literal('queued'),
  })
  .strict();

const runningRunObject = z
  .object({
    ...reportRunBase,
    status: z.literal('running'),
    startedAt: isoDateTimeSchema,
  })
  .strict();

const succeededRunObject = z
  .object({
    ...reportRunBase,
    status: z.literal('succeeded'),
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema,
    /**
     * **Required.** A succeeded run without a file is not representable, which
     * is the structural half of "a failed run is visible as a failed run": the
     * only way to have no file is to be `queued`, `running`, or `failed` with a
     * reason.
     */
    artifact: reportArtifactSchema,
  })
  .strict();

const failedRunObject = z
  .object({
    ...reportRunBase,
    status: z.literal('failed'),
    /**
     * **Optional, and it is the one member where that is true of a timestamp.**
     * A run can fail before it ever starts: the sweep gives up on a `queued` row
     * that has been waiting longer than any plausible backlog, and that row has
     * no start time because nothing started.
     *
     * The superseded shape required it, and the sweep satisfied the requirement
     * by writing `started_at := requested_at`. That is a real instant standing
     * for an event that did not happen, and the screen printed the consequence:
     * {@link reportRunDurationMs} subtracted the two and
     * `ReportRunProvenanceCell` rendered *"took 360m 0s"* for a report that was
     * never built — six hours of queue latency reported as six hours of work.
     * Absent is the truthful answer, and it is the answer this codebase insists
     * on everywhere else a measurement does not exist.
     */
    startedAt: isoDateTimeSchema.optional(),
    finishedAt: isoDateTimeSchema,
    /** **Required.** See {@link reportFailureSchema}. */
    failure: reportFailureSchema,
  })
  .strict();

/**
 * One attempt at producing a report.
 *
 * A discriminated union rather than one wide row with nullable columns. The wide
 * row can express "succeeded, no artifact" and "failed, no reason" — the two
 * states a screen renders as "no report", which is the sentence slice 13 exists
 * to stop being told. It can also express "failed, with a file", which is a
 * half-written workbook offered as a download.
 *
 * The union is `.strict()` member by member, so a `failed` run carrying an
 * `artifact` fails parsing rather than reaching a screen. The one cross-member
 * invariant that cannot be expressed by shape alone — a run's artifact being in
 * the format its definition asked for — is re-applied here, on the union,
 * because a check that lives in only one of two parse paths is absent exactly
 * when somebody parses the other way.
 */
export const reportRunSchema = z
  .discriminatedUnion('status', [
    queuedRunObject,
    runningRunObject,
    succeededRunObject,
    failedRunObject,
  ])
  .superRefine((run, ctx) => {
    if (run.status === 'succeeded' && run.artifact.format !== run.definition.format) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['artifact', 'format'],
        message: 'A run produced a file in a format its definition did not ask for',
      });
    }
    // Narrowed positively rather than by excluding the two open states: TypeScript
    // will not carry a `!==` chain into the body here, and a cast to get the
    // fields back would defeat the point of the union being a union.
    //
    // `startedAt` is checked for presence rather than assumed, because a failed
    // run may legitimately have none — see {@link failedRunObject}. A run that
    // never started cannot have finished before it started, so there is nothing
    // to compare and nothing to complain about.
    if (run.status === 'succeeded' || run.status === 'failed') {
      const startedAt: string | undefined = run.startedAt;
      if (startedAt !== undefined && Date.parse(run.finishedAt) < Date.parse(startedAt)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['finishedAt'],
          message: 'A run cannot finish before it starts',
        });
      }
    }
  });
export type ReportRun = z.infer<typeof reportRunSchema>;
export type ReportRunQueued = z.infer<typeof queuedRunObject>;
export type ReportRunRunning = z.infer<typeof runningRunObject>;
export type ReportRunSucceeded = z.infer<typeof succeededRunObject>;
export type ReportRunFailed = z.infer<typeof failedRunObject>;

/** Statuses a run never leaves. Derived, so a fifth status cannot be forgotten. */
export const REPORT_RUN_TERMINAL_STATUSES = ['succeeded', 'failed'] as const;

export function isReportRunSucceeded(run: ReportRun): run is ReportRunSucceeded {
  return run.status === 'succeeded';
}

export function isReportRunFailed(run: ReportRun): run is ReportRunFailed {
  return run.status === 'failed';
}

/**
 * Whether the run has stopped moving — for polling, and for "is it still going?"
 * copy.
 *
 * A type predicate rather than a `boolean`, so the caller that stops polling and
 * the caller that then reads `finishedAt` are the same check. Returning
 * `boolean` here compiled and left `finishedAt` unreachable without a cast,
 * which is how a second, hand-written status test gets written next to this one.
 */
export function isReportRunFinished(
  run: ReportRun,
): run is ReportRunSucceeded | ReportRunFailed {
  return run.status === 'succeeded' || run.status === 'failed';
}

/**
 * The file, or `null` — the **only** supported way to ask "is there a download".
 *
 * A guard rather than an optional-chained field access, so a caller cannot write
 * `run.artifact?.fileName` on a failed run, get `undefined`, and render an empty
 * download button beside no explanation. Narrowing forces the other branch to be
 * written, and {@link reportRunStatusDetail} is what belongs in it.
 */
export function reportRunArtifact(run: ReportRun): ReportArtifact | null {
  return isReportRunSucceeded(run) ? run.artifact : null;
}

/**
 * How long the run took, or `null` when that is not a question with an answer.
 *
 * `null` while it is still queued or running — and also for a **failed run that
 * never started**, which is the case {@link failedRunObject} exists to make
 * representable. There is no duration to report for a run the sweep gave up on
 * in the queue: it did not take six hours, it *waited* six hours, and printing
 * the wait under a label reading "took" is the same class of lie as a 0% bounce
 * rate on a workspace that never sent. The caller renders nothing rather than a
 * zero — `ReportRunProvenanceCell` already omits the line on `null`.
 */
export function reportRunDurationMs(run: ReportRun): number | null {
  if (!isReportRunFinished(run)) return null;
  if (run.startedAt === undefined) return null;
  return Date.parse(run.finishedAt) - Date.parse(run.startedAt);
}

/**
 * The status as a person reads it. Four states, four different sentences.
 *
 * `Failed` is a label in its own right and never falls back to a dash, a blank
 * cell or an empty list row. A list showing three runs where the middle one is
 * blank reads as a rendering bug; one showing "Failed" reads as a fact somebody
 * can act on — and it is the difference between "no report" and "the report
 * failed" that slice 13 "done when" 5 is entirely about.
 */
export const REPORT_RUN_STATUS_LABELS = {
  queued: 'Queued',
  running: 'Building…',
  succeeded: 'Ready',
  failed: 'Failed',
} as const satisfies Readonly<Record<ReportRun['status'], string>>;

/**
 * The sentence under the label. Empty string for the two states whose label
 * already says everything — never a placeholder like "—", which reads as a value
 * that failed to load.
 */
export function reportRunStatusDetail(run: ReportRun): string {
  if (run.status === 'failed') return REPORT_FAILURE_SUMMARIES[run.failure.code];
  if (run.status === 'queued') return 'Waiting to start.';
  // Not "this usually takes a few seconds": a run over a year of ledger does
  // not, and a promise the product breaks on the slow case is the copy defect
  // this codebase keeps finding. Say what is happening instead of how long —
  // and what is being read is the kind's own: slice 44's reads the period's
  // results, not the ledger, and names no ad account a workspace may not have.
  if (run.status === 'running') {
    return run.definition.kind === 'surface_results'
      ? 'Reading the results for this period.'
      : 'Reading the ledger for this period.';
  }
  return '';
}

/* -------------------------------------------------------------------------- */
/* The wire — asking for a run, and reading the runs there are                 */
/* -------------------------------------------------------------------------- */

/**
 * What a person's browser sends to start a run.
 *
 * **No `tenantId`.** See {@link reportDefinitionSchema}: the tenant comes from
 * the authenticated principal, and a field that is not in the shape is a field
 * nobody can forge. The window arrives fully resolved — the browser knows the
 * user's zone and the dates they picked, and resolving "last month" on the
 * client makes the moment of resolution explicit and recorded, which is the
 * whole argument of this slice applied to the one caller that has a clock and a
 * user in the same place.
 *
 * The response is **201**: a run is created, and it exists whether or not the
 * file does yet. The house rule is that 201 means something was created, and
 * here something was — that is exactly the distinction between this and the
 * streaming endpoint that was rejected.
 */
export const createReportRunRequestSchema = z
  .object({
    kind: reportKindSchema,
    format: reportFormatSchema.default('xlsx'),
    window: reportWindowSchema,
  })
  .strict();
export type CreateReportRunRequest = z.infer<typeof createReportRunRequestSchema>;

export const reportRunResponseSchema = z.object({ run: reportRunSchema });
export type ReportRunResponse = z.infer<typeof reportRunResponseSchema>;

export const reportRunListQuerySchema = paginationQuerySchema.extend({
  kind: reportKindSchema.optional(),
  /**
   * Filters the list. **The screen's default is no filter**: a list that hides
   * failed runs by default is a list that reports "no reports" for a workspace
   * whose every run failed.
   */
  status: reportRunStatusSchema.optional(),
});
export type ReportRunListQuery = z.infer<typeof reportRunListQuerySchema>;

export const reportRunListResponseSchema = paginated(reportRunSchema);
export type ReportRunListResponse = z.infer<typeof reportRunListResponseSchema>;

/**
 * Slice 44 — the kinds a run can be recorded as here, in contract order: the
 * members of `REPORT_KIND_VALUES` whose `report_kind` label the database
 * holds.
 *
 * **A kind the code knows and the database does not is not offered.** The
 * migration that adds a kind's label can reach a database later than the code
 * that offers it — slice 44's did — and a run of that kind is refused at its
 * insert, as a bare "something went wrong" that no second press repairs. So
 * the request panel offers only what this answers, and a kind appears the
 * moment its migration lands, with no deploy between. Pure over the labels,
 * so the refusal is testable without a database that lacks one.
 */
export function recordableReportKinds(labels: readonly string[]): ReportKind[] {
  return REPORT_KIND_VALUES.filter((kind) => labels.includes(kind));
}

/** `GET /reports/kinds`. */
export const reportKindsResponseSchema = z.object({ kinds: z.array(reportKindSchema) }).strict();
export type ReportKindsResponse = z.infer<typeof reportKindsResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The caption every sheet carries                                             */
/* -------------------------------------------------------------------------- */

/** One `label: value` line of the block printed at the top of every worksheet. */
export interface ReportCaptionLine {
  readonly label: string;
  readonly value: string;
}

/** What the caption needs. Assembled by the generator from the run it is executing. */
export interface ReportProvenance {
  readonly runId: string;
  readonly kind: ReportKind;
  readonly tenantName: string;
  readonly window: ReportWindow;
  /** When the bytes were produced — not when the window ends. */
  readonly generatedAt: Date;
  readonly trigger: ReportTrigger;
}

/**
 * The block that goes at the top of **every** worksheet, not just the first.
 *
 * Decision 4 of slice 13: *a spreadsheet outlives the screen it came from and
 * will be mailed to somebody who never saw the filters.* The rejected
 * alternative was a single cover sheet, and it is rejected because the first
 * thing anybody does with a multi-sheet workbook is copy one tab out of it — at
 * which point the cover sheet, and every statement of what the numbers cover,
 * stays behind.
 *
 * One function, so the sheets cannot disagree: the suite asserts the email and
 * WhatsApp sheets carry byte-identical caption blocks, which is only a
 * meaningful assertion because there is nowhere else for the lines to come from.
 *
 * "Generated" states the instant in the report's own zone. The window's zone is
 * used rather than the server's, because a reader comparing the generation time
 * against the window boundaries needs both on the same clock.
 */
export function reportCaptionLines(provenance: ReportProvenance): readonly ReportCaptionLine[] {
  return [
    { label: 'Report', value: REPORT_TITLES[provenance.kind] },
    { label: 'Workspace', value: provenance.tenantName },
    { label: 'Period', value: describeReportWindow(provenance.window) },
    {
      label: 'Generated',
      value: formatInstantInZone(provenance.generatedAt, provenance.window.timezone),
    },
    { label: 'Started by', value: describeReportTrigger(provenance.trigger) },
    { label: 'Run', value: provenance.runId },
  ];
}

/* -------------------------------------------------------------------------- */
/* The credit-usage report                                                     */
/* -------------------------------------------------------------------------- */

/** What a sheet says when the window held no movements for that channel. */
export const NO_MOVEMENTS_NOTE = 'No credit movements in this period.';

/** The worksheet each channel's movements go on. Total over `CHANNEL_VALUES`. */
export const CREDIT_USAGE_SHEET_NAMES = {
  email: 'Email credits',
  whatsapp: 'WhatsApp credits',
} as const satisfies Readonly<Record<Channel, string>>;

/**
 * The Results by surface sheets that are not a surface. Slice 44's two: the
 * Meta ad account's whole — Meta's unbroken figures, each person counted once
 * — before the ad surfaces it is made of, and Meta's other placements after
 * them, only when the account spent there. Slice 45's one, first: the
 * surfaces side by side — people reached, and spend where it is measured
 * (`surface-comparison.ts`). Named here rather than in the workbook, so the
 * report picker lists the sheets the workbook writes.
 */
export const SURFACE_RESULTS_SHEET_NAMES = {
  bySurface: 'By surface',
  account: 'Meta ad account',
  other: 'Other placements',
} as const;

/**
 * The sheets every Results by surface workbook has, in order: the comparison
 * first, then each built surface by its label, and the ad account's sheet
 * before the first surface that is not a message's.
 */
export function surfaceResultsSheetNames(): readonly string[] {
  const names: string[] = [SURFACE_RESULTS_SHEET_NAMES.bySurface];
  for (const surface of SURFACE_ORDER.filter(surfaceIsBuilt)) {
    if (SURFACE_CAPABILITY[surface].kind !== 'direct' && !names.includes(SURFACE_RESULTS_SHEET_NAMES.account)) {
      names.push(SURFACE_RESULTS_SHEET_NAMES.account);
    }
    names.push(SURFACE_CAPABILITY[surface].label);
  }
  return names;
}

/** How each movement type is headed in the totals block. Total over the ledger enum. */
export const LEDGER_ENTRY_TYPE_LABELS = {
  grant: 'Granted',
  reserve: 'Reserved',
  release: 'Released',
  consume: 'Consumed',
  refund: 'Refunded',
  adjustment: 'Adjusted',
} as const satisfies Readonly<Record<LedgerEntryType, string>>;

/** A column on the movements table. The writer sets widths from it; a CSV would too. */
export interface ReportColumn {
  readonly key: string;
  readonly header: string;
  readonly width: number;
  /** Right-aligned and written as a number rather than text. */
  readonly numeric: boolean;
}

/**
 * The key of the one numeric column. The totals block writes its figures under
 * this column and nowhere else — see {@link creditUsageTotalLines}.
 */
export const CREDIT_USAGE_AMOUNT_COLUMN_KEY = 'amount';

/**
 * The movement columns, in order, with the time column naming the zone.
 *
 * A function of the zone rather than a constant, because `When` on its own is
 * the ambiguity this slice refuses: a timestamp column in a spreadsheet with no
 * zone in its heading is read as the reader's local time, and the reader is in a
 * different country from the workspace often enough to matter.
 *
 * **No heading says cost, spend, charge or price** — `./credit.js`'s rule,
 * restated here because this is the file where those words would actually reach
 * a customer. A credit is Wend's metering unit; it has no exchange rate with the
 * tenant's AWS or Meta invoice, and five WhatsApp messages inside one Meta
 * conversation are five credits here and one line on Meta's bill.
 *
 * **THERE IS NO `Note` COLUMN, AND THAT IS THE CORRECTION, NOT THE OVERSIGHT.**
 * The superseded position was that the column belonged here and would fill in
 * when `credit_ledger` gained a `note` field — `CreditsService.listLedger`
 * states that hole, and `credit-usage.generator.ts` recorded both call sites so
 * whoever closed it would find them. Leaving the column standing in the meantime
 * was wrong for the reason this codebase keeps re-learning in other shapes: an
 * always-empty cell is not "no note was written", it is *no such field exists*,
 * and a spreadsheet has no way to say the second. Every workbook ever produced
 * carried a column that was blank on every row of every sheet, which reads to a
 * customer as data we lost. The column comes back the day the ledger has
 * something to put in it — add it here and add the cell in
 * {@link creditUsageRow}; those two lines are the whole change.
 */
export function creditUsageColumns(timezone: string): readonly ReportColumn[] {
  return [
    { key: 'createdAt', header: `When (${timezone})`, width: 26, numeric: false },
    { key: 'type', header: 'Movement', width: 14, numeric: false },
    { key: CREDIT_USAGE_AMOUNT_COLUMN_KEY, header: 'Credits', width: 10, numeric: true },
    { key: 'messageId', header: 'Message', width: 38, numeric: false },
    { key: 'campaignId', header: 'Campaign', width: 38, numeric: false },
    { key: 'idempotencyKey', header: 'Ledger key', width: 46, numeric: false },
  ];
}

/**
 * One ledger entry as the cells of one row, in {@link creditUsageColumns} order.
 *
 * It exists so the workbook writer and the suite that reconciles the workbook
 * against `credit_ledger` cell-for-cell are reading the same projection, and so
 * the request-started and schedule-started runs cannot produce different cells
 * from the same rows. A writer that formatted its own cells would make the
 * both-paths comparison a test of the writer rather than of the generator.
 *
 * `amount` is a **number**; every other cell is a string. A blank cell means
 * "this movement type has no such field" — a grant has no message — and never
 * "we do not know": no number column is ever blank, so a blank can never be
 * mistaken for a zero.
 *
 * `entry.note` is deliberately **not** projected, and the entry type still
 * carries it. Nothing writes it — `credit_ledger` has no such column — so a
 * cell for it would be blank on every row of every report, which is the one
 * thing a blank cell must never mean here. See {@link creditUsageColumns}.
 */
export function creditUsageRow(
  entry: CreditLedgerEntry,
  timezone: string,
): readonly (string | number)[] {
  return [
    formatInstantInZone(new Date(entry.createdAt), timezone),
    LEDGER_ENTRY_TYPE_LABELS[entry.type],
    entry.amount,
    entry.messageId ?? '',
    entry.campaignId ?? '',
    entry.idempotencyKey,
  ];
}

/**
 * Totals per movement type, **starting from zero for every type**.
 *
 * A `GROUP BY type` returns no row for a type nothing produced, so a channel
 * that never bounced would leave `release` undefined and print a blank where `0`
 * belongs — the same defect `recipientStatusCounts` and `campaignCreditTotals`
 * were both shaped to prevent. Here a zero is the truthful answer: the window
 * was read, and it contained no releases.
 *
 * Total over `LEDGER_ENTRY_TYPE_VALUES`, so a seventh entry type appears on the
 * sheet the day it is added rather than being silently dropped from the totals
 * while still being listed row by row above them.
 *
 * **This is a sum, and for `adjustment` a sum is a signed net.**
 * `creditLedgerEntrySchema` refuses a negative amount on every type except
 * `adjustment`, so five of these six numbers are magnitudes and one is not. That
 * asymmetry is arithmetically correct and, printed beside the other five under
 * one heading, is a lie a reader cannot see: a month holding a +500 correction
 * and a −500 correction totals 0, which is the same cell a month with no
 * corrections at all prints. Nothing renders this key directly for that reason
 * — {@link creditUsageTotalLines} is what a sheet prints, and it splits the
 * adjustment into its two directions. The signed net stays here because it is
 * what `net.balance` is folded from and because a consumer that wants the net
 * should get the net, not a magnitude.
 */
export function creditUsageTotalsByType(
  movements: readonly CreditLedgerEntry[],
): Readonly<Record<LedgerEntryType, number>> {
  const totals = Object.fromEntries(
    LEDGER_ENTRY_TYPE_VALUES.map((type) => [type, 0]),
  ) as Record<LedgerEntryType, number>;
  for (const movement of movements) {
    totals[movement.type] += movement.amount;
  }
  return totals;
}

/**
 * How the two directions of an adjustment are headed.
 *
 * Not `LEDGER_ENTRY_TYPE_LABELS.adjustment`, because one label over one number
 * is exactly what could not tell "no corrections" from "corrections that
 * cancelled". Both figures are printed as **magnitudes**, like the other five,
 * and the direction is carried by the words rather than by a minus sign a reader
 * has to notice.
 */
export const ADJUSTMENT_TOTAL_LABELS = {
  increase: 'Adjusted up',
  decrease: 'Adjusted down',
} as const;

/** One labelled figure in a sheet's totals block. */
export interface CreditUsageTotalLine {
  readonly label: string;
  readonly value: number;
}

/**
 * The totals block a sheet prints, in order — the one place that decides what
 * "Adjusted" means on paper.
 *
 * Derived from `LEDGER_ENTRY_TYPE_VALUES` rather than from the keys of
 * `totalsByType`, so a seventh entry type appears here the day it is added; and
 * `adjustment` is the one member expanded into two lines, because it is the one
 * type `creditLedgerEntrySchema` allows to be negative. Five magnitudes and one
 * signed net under a single heading is a totals block that prints `0` for a
 * period holding two corrections, which reads identically to a period holding
 * none — and a reader auditing corrections would close the file having
 * concluded the opposite of the truth.
 *
 * The rejected alternative was printing the signed net and letting the reader
 * infer: it is one row shorter and it is unreadable, because a net of 0 and an
 * absence of 0 are the same cell and nothing on the sheet distinguishes them.
 */
export function creditUsageTotalLines(
  section: Pick<CreditUsageSection, 'movements' | 'totalsByType'>,
): readonly CreditUsageTotalLine[] {
  const lines: CreditUsageTotalLine[] = [];
  for (const type of LEDGER_ENTRY_TYPE_VALUES) {
    if (type !== 'adjustment') {
      lines.push({ label: LEDGER_ENTRY_TYPE_LABELS[type], value: section.totalsByType[type] ?? 0 });
      continue;
    }
    let increases = 0;
    let decreases = 0;
    for (const movement of section.movements) {
      if (movement.type !== 'adjustment') continue;
      if (movement.amount >= 0) increases += movement.amount;
      else decreases -= movement.amount;
    }
    lines.push({ label: ADJUSTMENT_TOTAL_LABELS.increase, value: increases });
    lines.push({ label: ADJUSTMENT_TOTAL_LABELS.decrease, value: decreases });
  }
  return lines;
}

/* -------------------------------------------------------------------------- */
/* The structural rules — one implementation, two callers                      */
/* -------------------------------------------------------------------------- */

/** One thing wrong with a report's structure, and where in it. */
export interface CreditUsageStructuralIssue {
  readonly path: readonly (string | number)[];
  readonly message: string;
}

/**
 * The narrow shape the structural rules actually read.
 *
 * Narrower than the parsed types on purpose, so the rules can be run against a
 * report that is being *assembled* — before anything has been through zod —
 * without the checker quietly re-validating fields it has no opinion about.
 */
interface CreditUsageSectionShape {
  readonly channel: Channel;
  readonly movements: readonly { readonly channel: Channel }[];
  readonly totalsByType: Readonly<Partial<Record<LedgerEntryType, number>>>;
}

/**
 * What is wrong with one section, as messages. Empty means nothing is.
 *
 * **THIS FUNCTION IS THE GUARANTEE, AND THE SCHEMAS ARE ONE OF ITS CALLERS.**
 * That inversion is the correction, and it is worth stating what it replaced:
 * three headers in this slice described {@link creditUsageSectionSchema} and
 * {@link creditUsageReportSchema} as the thing that makes a mismatched section
 * or a missing channel impossible — and **nothing in the API ever parsed
 * either**. A generator assembled a report, handed it to a writer, and the
 * writer produced bytes; the schemas guarded the wire, and nothing was on the
 * wire. The guarantee was prose, which is worse than no guarantee: the next
 * reviewer checks the header, finds the case named, and moves on.
 *
 * A deep `parse` on the assembly path was the obvious repair and is rejected.
 * `MAX_MOVEMENTS_PER_REPORT` is 200,000 rows, and the cap exists precisely
 * because holding that many row objects is already the memory this process is
 * willing to spend; parsing them would allocate a second copy of every one to
 * check a rule that reads one field. So the rules live in a plain function that
 * walks the movements once and allocates nothing, {@link assertCreditUsageReport}
 * runs it on the assembly path, and the `superRefine`s below run the same
 * function so a report arriving as JSON is held to exactly the same rules by
 * exactly the same code.
 */
export function creditUsageSectionIssues(
  section: CreditUsageSectionShape,
): readonly CreditUsageStructuralIssue[] {
  const issues: CreditUsageStructuralIssue[] = [];

  const missingTotals = LEDGER_ENTRY_TYPE_VALUES.filter(
    (type) => typeof section.totalsByType[type] !== 'number',
  );
  if (missingTotals.length > 0) {
    issues.push({
      path: ['totalsByType'],
      message: `The totals must carry every movement type, zeros included; ${missingTotals.join(', ')} is absent`,
    });
  }

  section.movements.forEach((movement, index) => {
    if (movement.channel !== section.channel) {
      issues.push({
        path: ['movements', index, 'channel'],
        message: `The ${section.channel} sheet cannot carry a ${movement.channel} movement`,
      });
    }
  });

  return issues;
}

/**
 * What is wrong with the *set* of sections: a missing channel, two sections for
 * one, or the two in the wrong order. Section-level rules are
 * {@link creditUsageSectionIssues}'s.
 *
 * **The order clause is not tidiness, and it was the half this function was
 * missing.** Three headers in this slice — this file's own
 * {@link creditUsageReportSchema} doc and `credit-usage.workbook.ts`'s "ONE
 * WORKSHEET PER CHANNEL, IN `CHANNEL_VALUES` ORDER" — stated the order as a
 * guarantee while nothing checked it; the only thing holding it up was
 * {@link creditUsageReport}'s `CHANNEL_VALUES.map`, a habit of the one current
 * builder. `writeCreditUsageWorkbook` is exported and driven directly with
 * hand-built reports, and it writes sheets in section order, so a report
 * assembled `[whatsapp, email]` produced a workbook whose tabs contradicted
 * three headers and passed every check. Tab order is what a reader navigates by
 * across months of archived workbooks, and "the sheets moved" is indistinguishable
 * from "somebody edited this file" once it is out of our hands.
 */
export function creditUsageChannelCoverageIssues(report: {
  readonly sections: readonly { readonly channel: Channel }[];
}): readonly CreditUsageStructuralIssue[] {
  const issues: CreditUsageStructuralIssue[] = [];
  const channels = report.sections.map((section) => section.channel);

  const missing = CHANNEL_VALUES.filter((channel) => !channels.includes(channel));
  if (missing.length > 0) {
    issues.push({
      path: ['sections'],
      message: `A credit usage report carries one section per channel; ${missing.join(', ')} is missing`,
    });
  }
  if (new Set(channels).size !== channels.length) {
    issues.push({
      path: ['sections'],
      message: 'A channel cannot have two sections — its movements would be split across two sheets',
    });
  }
  // Asked only once the set is right, so a report missing WhatsApp is told that
  // and not also told its order is wrong — two messages for one fault reads as
  // two faults.
  if (issues.length === 0 && channels.join(',') !== CHANNEL_VALUES.join(',')) {
    issues.push({
      path: ['sections'],
      message: `The sections must be in ${CHANNEL_VALUES.join(', ')} order — the sheets of every workbook are written in this order and are read across months of them`,
    });
  }

  return issues;
}

/** Every structural rule, section-level ones included, for a whole report. */
export function creditUsageReportIssues(report: {
  readonly sections: readonly CreditUsageSectionShape[];
}): readonly CreditUsageStructuralIssue[] {
  return [
    ...creditUsageChannelCoverageIssues(report),
    ...report.sections.flatMap((section, index) =>
      creditUsageSectionIssues(section).map((issue) => ({
        path: ['sections', index, ...issue.path],
        message: issue.message,
      })),
    ),
  ];
}

/**
 * Returns the report, or throws naming every rule it broke.
 *
 * Throws rather than returning a result, because there is no useful thing a
 * caller could do with a structurally broken report except refuse it — and in
 * the generator a throw is already the recorded-failure path, so a report that
 * could not be assembled becomes a *failed run with a reason* rather than a
 * workbook whose WhatsApp tab holds email movements.
 */
export function assertCreditUsageReport(report: CreditUsageReport): CreditUsageReport {
  const issues = creditUsageReportIssues(report);
  if (issues.length > 0) {
    throw new Error(
      `This credit usage report is not one a workbook can be written from: ${issues
        .map((issue) => issue.message)
        .join('; ')}.`,
    );
  }
  return report;
}

/**
 * One channel's page of the report: the movements, and what they add up to.
 *
 * The section refuses a movement whose channel is not its own. That is decision
 * 2 of the slice made structural rather than trusted to the query: an email
 * `consume` on the WhatsApp sheet would be summed into a column headed WhatsApp
 * credits, and nothing downstream could tell. Parsing refuses it, and so does
 * {@link assertCreditUsageReport} on the path that never parses — both from
 * {@link creditUsageSectionIssues}, which is the single implementation.
 *
 * `net` is folded with `sumLedgerEntries` from `./credit.js` — the same
 * arithmetic `credit_balances` is maintained with and the same the nightly
 * reconciliation re-sums with. Restating it here would create a third opinion
 * about what a `release` does, and the report would be the one nobody notices
 * is wrong, because nobody re-adds a spreadsheet by hand.
 */
export const creditUsageSectionSchema = z
  .object({
    channel: channelSchema,
    /** Every movement in the window, oldest first, exactly as the ledger holds them. */
    movements: z.array(creditLedgerEntrySchema),
    /**
     * Per type, zeros included, and keyed by the ledger's own enum rather than
     * by `string` — a totals block with a key nothing in `credit_ledger` can
     * produce is a column the writer would happily print a heading for.
     *
     * **The inferred type says every type is present; zod's `record` does not
     * enforce that**, and the gap was real rather than theoretical — a payload
     * missing `release` parsed cleanly while the type told the writer the key
     * was there, so the totals row would have printed `undefined` or a blank in
     * the one cell that means "nothing bounced". The type being right is not the
     * same as the data being right.
     *
     * What closes it is {@link creditUsageSectionIssues}, not the `superRefine`
     * below. The superseded sentence here said "the refinement below closes it",
     * and that was only ever true of a report someone had `parse`d — which is a
     * report that arrived on a wire, and none does. The refinement calls the
     * function; {@link assertCreditUsageReport} calls the same function on the
     * assembly path, which is the path every workbook in this deployment is
     * written from.
     */
    totalsByType: z.record(ledgerEntryTypeSchema, z.number().int()),
    /**
     * What the window did to this channel's two numbers — `balance` and
     * `reserved` — as a delta, not as a balance. A report of a window cannot
     * state a closing balance without reading every entry before the window too,
     * and a number that looks like a balance but is a delta is worse than no
     * number at all.
     */
    net: z.object({ balance: z.number().int(), reserved: z.number().int() }).strict(),
  })
  .strict()
  .superRefine((section, ctx) => {
    for (const issue of creditUsageSectionIssues(section)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...issue.path], message: issue.message });
    }
  });
export type CreditUsageSection = z.infer<typeof creditUsageSectionSchema>;

/** Builds a channel's section from its movements. One place, so both paths agree. */
export function creditUsageSection(
  channel: Channel,
  movements: readonly CreditLedgerEntry[],
): CreditUsageSection {
  const net: CreditAmounts =
    movements.length === 0 ? ZERO_CREDIT_AMOUNTS : sumLedgerEntries(movements);
  return {
    channel,
    movements: [...movements],
    totalsByType: creditUsageTotalsByType(movements),
    net: { balance: net.balance, reserved: net.reserved },
  };
}

/**
 * The whole credit-usage report: its provenance, and one section per channel.
 *
 * **Exactly one section per member of `CHANNEL_VALUES`, in that order, always.**
 * Not "one per channel that moved something": a workbook missing its WhatsApp
 * tab says nothing about WhatsApp, and the reader supplies the missing statement
 * themselves — usually the wrong one.
 *
 * **What enforces that is {@link creditUsageChannelCoverageIssues}, and the
 * refinement below is one of its two callers.** The superseded sentence here
 * named the refinement itself as "what makes the one-worksheet-per-channel
 * decision a property of the data rather than a habit of the writer" — the
 * claim the rest of this file spends a paragraph retracting, because a
 * refinement runs under `parse` and nothing in the API parses a credit-usage
 * report. Left standing it argued the reverse of the truth: that the two
 * {@link assertCreditUsageReport} calls on the assembly path are belt-and-braces
 * over a zod guarantee and can be deleted. They are the guarantee. This schema
 * guards the wire nothing currently crosses, and it is kept so the day something
 * does cross it, it meets the same three rules from the same three functions.
 */
export const creditUsageReportSchema = z
  .object({
    kind: z.literal('credit_usage'),
    window: reportWindowSchema,
    generatedAt: isoDateTimeSchema,
    sections: z.array(creditUsageSectionSchema),
  })
  .strict()
  .superRefine((report, ctx) => {
    // Only the cross-section rules: each element has already been through
    // `creditUsageSectionSchema`, which runs the section-level ones.
    for (const issue of creditUsageChannelCoverageIssues(report)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...issue.path], message: issue.message });
    }
  });
export type CreditUsageReport = z.infer<typeof creditUsageReportSchema>;

/**
 * Assembles the report from movements already grouped by channel.
 *
 * Iterating `CHANNEL_VALUES` rather than the keys of `byChannel` is what
 * guarantees the empty channel gets its sheet: a channel absent from the map
 * produces a section with no movements and zero totals, which is the true
 * statement, rather than no section at all, which is silence.
 *
 * It ends by asserting its own output. That looks redundant next to the
 * iteration above — which is exactly why it is here: the iteration is what
 * makes the rules hold *today*, and the assertion is what makes them hold after
 * somebody changes it. This is the only place in the API a credit-usage report
 * is built, so this call is where the guarantee three headers describe actually
 * becomes one. It costs one channel comparison per movement and allocates
 * nothing; see {@link creditUsageSectionIssues} for why it is not a `parse`.
 */
export function creditUsageReport(
  window: ReportWindow,
  generatedAt: Date,
  byChannel: Readonly<Partial<Record<Channel, readonly CreditLedgerEntry[]>>>,
): CreditUsageReport {
  return assertCreditUsageReport({
    kind: 'credit_usage',
    window,
    generatedAt: generatedAt.toISOString(),
    sections: CHANNEL_VALUES.map((channel) => creditUsageSection(channel, byChannel[channel] ?? [])),
  });
}
